import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { Search, BrainCircuit, MessageSquare, Sparkles, ChevronRight } from 'lucide-react';




function ResultRow({ onClick, color, icon, title, subtitle, isSelected }: {
  onClick: () => void;
  color: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  isSelected?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full p-3 flex items-center gap-4 rounded-2xl transition-all text-left group ${
        isSelected 
          ? 'bg-celestial-saturn/20 border border-celestial-saturn/40 shadow-lg shadow-celestial-saturn/10' 
          : 'hover:bg-white/5'
      }`}
    >
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center p-2 shadow-lg shrink-0`}>
        {React.isValidElement(icon) ? React.cloneElement(icon, { size: 22 } as any) : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm tracking-tight truncate ${isSelected ? 'font-black text-white' : 'font-black text-white/90'}`}>{title}</div>
        <div className="text-xs uppercase tracking-widest truncate text-white/55">{subtitle}</div>
      </div>
      <div className={`${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <ChevronRight size={16} className="text-white/40" />
      </div>
    </button>
  );
}

// ─── Main Spotlight component ───

export function Spotlight({ isOpen, onClose, onSelect, onOpenConversation, apps, t }: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  onOpenConversation: (convId: string) => void;
  apps: any[];
  t: any;
}) {
  const [query, setQuery] = useState('');
  const [conversations, setConversations] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [localConvs, setLocalConvs] = useState<any[]>([]);
  const [localSkills, setLocalSkills] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  
  // Step 4: 键盘导航状态和结果列表
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const groupLabel = (key: string, fallback: string) => (t as any)?.[key] || fallback;
  
  const openConversation = useCallback((convId: string) => {
    onOpenConversation(convId);
    onClose();
  }, [onOpenConversation, onClose]);

  const handleAppSelect = useCallback((appId: string) => {
    onSelect(appId);
    onClose();
  }, [onSelect, onClose]);

  const handleSkillSelect = useCallback(() => {
    onSelect('skills');
    onClose();
  }, [onSelect, onClose]);
  
  const filteredApps = apps.filter(app => {
    const q = query.trim().toLowerCase();
    return !q || app.label.toLowerCase().includes(q) || app.id.toLowerCase().includes(q);
  });
  
  const allResults = React.useMemo(() => {
    const items: Array<{ type: 'app'|'conv'|'skill'; id: string; label: string }> = [];
    filteredApps.forEach(app => items.push({ type: 'app', id: app.id, label: app.label }));
    conversations.forEach(c => items.push({ type: 'conv', id: c.id, label: c.title || '' }));
    skills.forEach(s => items.push({ type: 'skill', id: s.name, label: s.name }));
    return items;
  }, [filteredApps, conversations, skills]);
  
  useEffect(() => { setSelectedIndex(0); }, [isOpen]);

  
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    Promise.allSettled([
      import('../../services/conversationService').then(m => m.listConversations()),
      fetch('/api/skills').then(r => r.ok ? r.json() : Promise.reject()),
    ]).then(([convRes, skillRes]) => {
      if (cancelled) return;
      if (convRes.status === 'fulfilled') setLocalConvs(convRes.value || []);
      if (skillRes.status === 'fulfilled') setLocalSkills((skillRes.value?.skills) || []);
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setConversations([]);
      setSkills([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      fetch(`/api/search?q=${encodeURIComponent(q)}&limit=20`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then((data: any) => {
          if (cancelled) return;
          setConversations(data.conversations || []);
          setSkills(data.skills || []);
        })
        .catch(() => {
          if (cancelled) return;
          const ql = q.toLowerCase();
          setConversations(localConvs.filter((c: any) => (c.title || '').toLowerCase().includes(ql)));
          setSkills(localSkills.filter((s: any) =>
            (s.name || '').toLowerCase().includes(ql) ||
            (s.description || '').toLowerCase().includes(ql)
          ));
        })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, localConvs, localSkills]);
  
  // Step 4: 键盘导航
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % allResults.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + allResults.length) % allResults.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (allResults[selectedIndex]) {
          const item = allResults[selectedIndex];
          if (item.type === 'app') handleAppSelect(item.id);
          else if (item.type === 'conv') openConversation(item.id);
          else if (item.type === 'skill') handleSkillSelect();
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, allResults, selectedIndex, handleAppSelect, openConversation, handleSkillSelect]);
  
  // 辅助函数：根据类型获取颜色/图标/副标题
  const getColorForItemType = (type: string) => {
    if (type === 'app') return 'from-blue-500 to-indigo-600';
    if (type === 'conv') return 'from-green-500 to-emerald-600';
    if (type === 'skill') return 'from-emerald-500 to-teal-600';
    return 'from-gray-500 to-gray-600';
  };
  
  const getIconForItemType = (type: string) => {
    if (type === 'app') return <BrainCircuit size={22} />;
    if (type === 'conv') return <MessageSquare size={22} />;
    if (type === 'skill') return <Sparkles size={22} />;
    return <Search size={22} />;
  };
  
  const getSubtitleForItemType = (type: string) => {
    if (type === 'app') return groupLabel('neuralApp', '神经应用');
    if (type === 'conv') return groupLabel('searchChatSub', '历史对话');
    if (type === 'skill') return groupLabel('skill', '技能');
    return groupLabel('searchHint', '搜索项');
  };
  
  const handleSelectAllResults = useCallback((item: { type: string; id: string; label: string }) => {
    if (item.type === 'app') handleAppSelect(item.id);
    else if (item.type === 'conv') openConversation(item.id);
    else if (item.type === 'skill') handleSkillSelect();
  }, [handleAppSelect, openConversation, handleSkillSelect]);

  const q = query.trim().toLowerCase();
  
  const hasResults = allResults.length > 0;

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4 pointer-events-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: -20, scale: 0.95 }}
        animate={{ y: 0, scale: 1 }}
        className="w-full max-w-xl glass-dark border border-white/10 rounded-[2rem] overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.8)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 flex items-center gap-4 border-b border-white/5">
          <Search size={24} className="text-white/40" />
          <input
            autoFocus
            placeholder={t.searchNeuralHub || "搜索小枫中枢..."}
            className="flex-1 bg-transparent border-none outline-none text-xl font-bold text-white placeholder:text-white/45"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {searching && (
            <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
          )}
          <div className="px-2 py-1 bg-white/5 rounded text-xs font-black text-white/40 tracking-widest border border-white/5">ESC</div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 custom-scrollbar max-h-[55vh]">
          {!q && (
            <div className="p-8 text-center text-white/35 text-xs font-black uppercase tracking-widest">
              {t.searchHint || '输入以搜索应用、对话与技能'}
            </div>
          )}

          {q && !searching && !hasResults && (
            <div className="p-12 text-center text-white/45">
              <BrainCircuit size={48} className="mx-auto mb-4 opacity-10" />
              <p className="text-xs font-black uppercase tracking-widest">{t.noNeuralNodes || '未找到神经节点'}</p>
            </div>
          )}

          {/* Unified result list with global index tracking */}
          {!q && !hasResults && (
            <div className="p-8 text-center text-white/35 text-xs font-black uppercase tracking-widest">
              {t.searchHint || '输入以搜索应用、对话与技能'}
            </div>
          )}
          
          {allResults.map((item, i) => (
            <ResultRow
              key={`${item.type}-${item.id}`}
              onClick={() => handleSelectAllResults(item)}
              color={getColorForItemType(item.type)}
              icon={getIconForItemType(item.type)}
              title={item.label}
              subtitle={getSubtitleForItemType(item.type)}
              isSelected={selectedIndex === i}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
